
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Schedule } from '@/modules/studentCalendar';

export interface CalendarEvent {
  eventId: string;
  startDate: Date;
  endDate: Date;
  color: string;
  monthHtml: string;
  weekShortHtml: string;
  weekLongHtml: string;
  dateShortHtml: string;
  dateLongHtml: string;
}

export interface TimeSchedule extends CalendarEvent {
  startTimeIndex: number;
  endTimeIndex: number;
}

@Component
export default class extends Vue {

  protected get isLarge(): boolean { return (this as any).$mq === 'large'; }
  protected get isSmall(): boolean { return (this as any).$mq === 'small'; }
  protected get isMini(): boolean { return (this as any).$mq === 'mini'; }

  protected get events(): CalendarEvent[] {
    const firstDate = this.firstDate();
    const lastDate = this.lastDate();
    if (this.allEvents && firstDate !== null && lastDate !== null) {
      lastDate.setHours(23);
      lastDate.setMinutes(59);
      lastDate.setSeconds(59);
      const unixtimeMin = firstDate.getTime();
      const unixtimeMax = lastDate.getTime();
      return this.allEvents.filter((calendarEvent: CalendarEvent) => {
        return calendarEvent.startDate.getTime() >= unixtimeMin && calendarEvent.endDate.getTime() <= unixtimeMax;
      });
    }
    return [];
  }


  protected get timeSchedules(): TimeSchedule[] {
    const firstDate = this.firstDate();
    const lastDate = this.lastDate();
    if (firstDate === null || lastDate === null) { return []; }
    const timeSchedules: TimeSchedule[] = [];
    this.events.forEach((calendarEvent: CalendarEvent, calendarIndex: number) => {
      let startTimeIndex = 0;
      let endTimeIndex = 0;
      const date = new Date(firstDate);
      while (date.getTime() !== lastDate.getTime()) {
        const time = this.TIMES.find((nowTime: number, timeIndex: number) => {
          const checkNowDate = new Date(date);
          checkNowDate.setHours(this.convertHour(nowTime));
          checkNowDate.setMinutes(this.convertMinutes(nowTime));
          const nextTime = this.TIMES.length - 1 > timeIndex ? this.TIMES[timeIndex + 1] : nowTime;
          const checkNextDate = new Date(date);
          checkNextDate.setHours(this.convertHour(nextTime));
          checkNextDate.setMinutes(this.convertMinutes(nextTime));
          if (
            checkNowDate.getTime() <= calendarEvent.startDate.getTime() &&
            checkNextDate.getTime() > calendarEvent.startDate.getTime()
          ) {
            startTimeIndex = timeIndex;
            return true;
          }
          return false;
        });

        if (time !== undefined) {
          const endTime = this.TIMES.find((nowTime: number, timeIndex: number) => {
            const checkNowDate = new Date(date);
            checkNowDate.setHours(this.convertHour(nowTime));
            checkNowDate.setMinutes(this.convertMinutes(nowTime));
            const nextTime = this.TIMES.length - 1 > timeIndex ? this.TIMES[timeIndex + 1] : nowTime;
            const checkNextDate = new Date(date);
            checkNextDate.setHours(this.convertHour(nextTime));
            checkNextDate.setMinutes(this.convertMinutes(nextTime));
            if (
              checkNowDate.getTime() < calendarEvent.endDate.getTime() &&
              checkNextDate.getTime() >= calendarEvent.endDate.getTime()
            ) {
              endTimeIndex = timeIndex;
              return true;
            }
            return false;
          });
          if (endTime !== undefined) {
            const size = endTimeIndex - startTimeIndex + 1;
            timeSchedules.push(Object.assign({
              startTimeIndex,
              endTimeIndex,
            }, calendarEvent));
          }
          break;
        }
        date.setDate(date.getDate() + 1);
      }
    });
    return timeSchedules;
  }

  protected get viewDates(): Date[][] {

    const firstDate = this.firstDate();
    const lastDate = this.lastDate();
    if (firstDate === null || lastDate === null) { return []; }

    const viewDates: Date[][] = [];
    const date = new Date(firstDate);
    viewDates.push([]);
    while (date.getTime() !== lastDate.getTime()) {
      viewDates[viewDates.length - 1].push(new Date(date));
      date.setDate(date.getDate() + 1);
      if (viewDates[viewDates.length - 1].length === this.headerWeeks.length) {
        viewDates.push([]);
      }
    }
    viewDates[viewDates.length - 1].push(new Date(date));
    return viewDates;
  }

  protected get viewDateFormat(): string {
    return this.dateFormat(this.viewDate);
  }
    protected readonly TIMES = [
    //  0,  0.5,
    //  1,  1.5,
    //  2,  2.5,
    //  3,  3.5,
    //  4,  4.5,
    //  5,  5.5,
    //  6,  6.5,
     7,  7.5,
     8,  8.5,
     9,  9.5,
    10, 10.5,
    11, 11.5,
    12, 12.5,
    13, 13.5,
    14, 14.5,
    15, 15.5,
    16, 16.5,
    17, 17.5,
    18, 18.5,
    19, 19.5,
    20, 20.5,
    21, 21.5,
    22, 22.5,
    23, 23.5,
  ];

  @Prop({required: true})
  protected allEvents: CalendarEvent[];
  protected readonly WEEK_NAMES = ['日', '月', '火', '水', '木', '金', '土'];

  @Prop()
  protected headerWeeks: number[];

  @Prop({ required: true })
  protected date: Date; // HACK: private でいいけど、private にすると lint で怒られる。仕方ないので protected。
  protected viewDate: Date = new Date();

  public firstDate(): Date | null {
    const firstDate = this.minDate();
    if (firstDate === null) { return null; }
    while (this.headerWeeks.indexOf(firstDate.getDay()) !== 0) {
      firstDate.setDate(firstDate.getDate() - 1);
    }
    return firstDate;
  }

  public lastDate(): Date | null {
    const lastDate = this.maxDate();
    if (lastDate === null) { return null; }
    while (this.headerWeeks.indexOf(lastDate.getDay()) !== this.headerWeeks.length - 1) {
      lastDate.setDate(lastDate.getDate() + 1);
    }
    lastDate.setHours(0);
    lastDate.setMinutes(0);
    lastDate.setSeconds(0);
    return lastDate;
  }

  protected minDate(): Date | null {
    if (!this.viewDate) { return null; }
    return new Date(this.viewDate);
  }

  protected maxDate(): Date | null {
    if (!this.viewDate) { return null; }
    return new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate());
  }

  protected dateFormat(date: Date): string {
    return '';
  }

  protected enableEvent(calendarEvent: CalendarEvent, nowDate: Date) {
    return (
      calendarEvent.startDate.getFullYear() === nowDate.getFullYear() &&
      calendarEvent.startDate.getMonth() === nowDate.getMonth() &&
      calendarEvent.startDate.getDate() === nowDate.getDate()
    );
  }

  protected convertHour(time: number): number {
    return parseInt(time.toString().split('.')[0], 10);
  }

  protected convertMinutes(time: number): number {
    const tmp = time.toString().split('.');
    if (tmp.length === 1) { return 0; }
    return Math.floor(60 * (parseInt(tmp[1], 10) / 10));
  }

  @Watch('date')
  private onChangedDate() {
    this.viewDate = this.date;
  }

  @Watch('viewDate')
  private onChangedViewDate() {
    this.$emit('update:date', this.viewDate);
  }

  private created() {
    this.viewDate = this.date;
  }
}
